<template>
  <v-dialog v-model="show" persistent fullscreen>
    <v-card>
      <v-card-title>Customize print estimate</v-card-title>
      <v-card-text class="pa-0" v-if="form">
        <v-card flat height="calc(100vh - 112px)">
          <v-container fluid class="pa-0">
            <v-row dense no-gutters>
              <v-col cols="5">
                <div style="height:calc(100vh - 112px)" class="pt-4 px-4 overflow-y">
                  <v-expansion-panels>

                    <v-expansion-panel>
                      <v-expansion-panel-header>Margins</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-container>
                          <v-row>
                            <v-col cols="3">
                              <decimal-number-input label="Top" :precision="2" prefix="" suffix="in" v-model="margins.top" hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <decimal-number-input label="Left" :precision="2" prefix="" suffix="in" v-model="margins.left" hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <decimal-number-input label="Bottom" :precision="2" prefix="" suffix="in" v-model="margins.bottom" hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <decimal-number-input label="Right" :precision="2" prefix="" suffix="in" v-model="margins.right" hide-details/>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel>
                      <v-expansion-panel-header>Top</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-container>
                          <v-row>
                            <v-col cols="6">
                              <v-select label="Style" :items="[{text:'Company/Title',value:0}, {text:'Title/Company',value:1}]" v-model="top.style" outlined dense/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="4">
                              <v-checkbox label="Include logo" v-model="top.company.logo" dense hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <decimal-number-input label="Height" :disabled="!top.company.logo" :precision="0" prefix="" suffix="px" v-model="top.company.logoHeight" hide-details/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="6">
                              <v-checkbox label="Include company name" v-model="top.company.name.show" dense hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <v-checkbox label="Bold" v-model="top.company.name.bold" :disabled="!top.company.name.show" dense hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <decimal-number-input label="Size" :disabled="!top.company.name.show" :precision="0" suffix="%" prefix="" v-model="top.company.name.size" hide-details/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="6">
                              <v-checkbox label="Include address" v-model="top.company.address" dense hide-details/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="6">
                              <v-checkbox label="Include phone" v-model="top.company.phone" dense hide-details/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="6">
                              <v-checkbox label="Include email" v-model="top.company.email" dense/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="3">
                              <decimal-number-input label="Title size" :precision="0" suffix="%" prefix="" v-model="top.title.size" hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <v-checkbox label="Bold" v-model="top.title.bold" dense hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <v-checkbox label="Shaded" v-model="top.title.shaded" dense hide-details/>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel @change="infoPanelChange">
                      <v-expansion-panel-header>Info</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-container>
                          <v-row>
                            <v-col cols="6">
                              <v-select label="Position" :items="[{text:'Top right',value:0}, {text:'Beside Bill to',value:1}, {text:'Below Bill to',value:2}]" v-model="info.position" outlined dense
                                        @change="info.position===2?info.box=true:''"/>
                            </v-col>
                            <v-col cols="4">
                              <v-checkbox label="Box format" v-model="info.box" dense hide-details :disabled="info.position===2"/>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col class="mt-4" style="border:1px solid grey">
                              <v-chip-group column multiple v-model="info.fields" ref="fields" @change="arrangeSelectedFields">
                                <template v-for="field in fields">
                                  <v-chip filter outlined :value="field.name" :key="field.name">{{field.title}}</v-chip>
                                </template>
                              </v-chip-group>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="4">
                              <v-checkbox label="Include Ship to" v-model="info.shipTo"/>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel>
                      <v-expansion-panel-header>Titles and values</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-container>
                          <v-row align="center">
                            <v-col cols="3" align="right" class="pt-7"><strong>Titles</strong></v-col>
                            <v-col cols="3">
                              <v-checkbox label="Lines" v-model="title.lines" hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <v-checkbox label="Shaded" v-model="title.shaded" hide-details/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="3" offset="3">
                              <v-checkbox label="Bold" v-model="title.bold" hide-details/>
                            </v-col>
                            <v-col cols="3">
                              <v-checkbox label="Uppercase" v-model="title.upperCase" hide-details/>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="4">
                              <v-checkbox label="Box values" v-model="form.valueLines" hide-details/>
                            </v-col>
                            <v-col cols="4">
                              <v-checkbox label="Column dividers" v-model="form.columnDividers" hide-details :disabled="!form.valueLines"/>
                            </v-col>
                            <v-col cols="4">
                              <v-checkbox label="Box totals" v-model="form.totalLines" hide-details :disabled="!form.valueLines"/>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel @change="columnPanelChange">
                      <v-expansion-panel-header>Columns</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-container>
                          <v-row>
                            <v-col class="mt-4" style="border:1px solid grey">
                              <v-chip-group column multiple v-model="form.columns" ref="columns" @change="arrangeSelectedColumns">
                                <template v-for="column in columns">
                                  <v-chip filter outlined :value="column.name" :key="column.name">{{column.title}}</v-chip>
                                </template>
                              </v-chip-group>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                  </v-expansion-panels>
                </div>
              </v-col>

              <v-col cols="7" class="grey lighten-3">
                <div style="height:calc(100vh - 112px)" class="pa-4 overflow-y">
                  <v-card width="100%" elevation="10">
                    <div v-html="sample" class="ma-2 pa-6"/>
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-card-text>
      <v-divider/>
      <v-card-actions class="pr-6">
        <v-spacer></v-spacer>
        <v-btn plain color="primary" @click="show=false;$emit('done')">Cancel</v-btn>
        <v-btn color="primary" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import estimatesPrintHTML from '@/views/sales/estimates/SalesEstimatesPrintHTML'
import sortableJS from 'sortablejs';

export default {
  name: 'SalesEstimatesFormSetup',
  mixins: [estimatesPrintHTML],
  data() {
    return {
      show: true,
      dirty: false,
      company: {},
      estimate: {type: 'Estimate', number:55, date: '2022-01-01', expires:'2022-01-31',salesRepName:'Sales rep',
                billToAddress:'Joe Customer\n123 Main Street\nToronto ON  M5V 2Y6', shipToAddress:'Joe Customer\n123 Main Street\nToronto ON  M5V 2Y6',
                memo: 'Message to customer',
                lines:[{sku:'SKU123',description:'Canadian Whisky',lot:'20212301',taxCodeId:'1',quantity:4,precision:0,unitPrice:89.50,amount:358},
                       {sku:'SKU234',description:'Gin',lot:'20214001',taxCodeId:'1',quantity:6,precision:0,unitPrice:40.50,amount:250}],
                       taxLines:[{id:1,name:'HST',percent:13,amount:79.04,onAmount:608}],
                       discount:{amount:0,percent:0,value:0}},
      taxes: [{id:'1',name:'HST',salesRate:13}],

      items: ['number','date','due','expires'],
      infoPanelSortable: null,
      columnPanelSortable: null,

      sample: '',
    }
  },
  watch: {
    form: {
      handler() {
        this.dirty = true;
        this.refresh();
      },
      immediate: true,
      deep: true
    }
  },
  async created() {
    const results = await this.$apiCall(`query SalesEstimatesFormSetup {
                                            company {name,address,city,province,postalCode,email,phone,logoURL}
                                          }`,
                                        {});
    if (!results.error)
      this.company = results.data.company;
    this.refresh();
  },
  beforeDestroy() {
    if (this.infoPanelSortable)
      this.infoPanelSortable.destroy();
    if (this.columnPanelSortable)
      this.columnPanelSortable.destroy();
  },
  methods: {
    infoPanelChange() {
      if (!this.infoPanelSortable) {
        const _self = this;
        this.infoPanelSortable = sortableJS.create(this.$refs.fields.$el.querySelector(`.v-slide-group__content`), {
          onEnd({newIndex, oldIndex}) {
            const infoField = _self.fields.splice(oldIndex, 1)[0];
            _self.fields.splice(newIndex, 0, infoField);
            _self.arrangeSelectedFields();
            _self.refresh();
          }
        })
      }
    },
    arrangeSelectedFields() {
      this.info.fields = this.fields.filter(field => this.info.fields.some(f => f === field.name)).map(field => field.name);
    },
    columnPanelChange() {
      if (!this.columnPanelSortable) {
        const _self = this;
        this.columnPanelSortable = sortableJS.create(this.$refs.columns.$el.querySelector(`.v-slide-group__content`), {
          onEnd({newIndex, oldIndex}) {
            const column = _self.columns.splice(oldIndex, 1)[0];
            _self.columns.splice(newIndex, 0, column);
            _self.arrangeSelectedColumns();
            _self.refresh();
          }
        })
      }
    },
    arrangeSelectedColumns() {
      this.form.columns = this.columns.filter(column => this.form.columns.some(f => f === column.name)).map(column => column.name);
    },
    refresh() {
      this.sample = this.print(this.company, this.estimate, this.taxes);
    },
    async save() {
      const results = await this.$apiCall(`mutation SalesEstimatesFormSetup($form: String!) {
                                             printFormSet(name:"estimate", form: $form)
                                           }`, {form: JSON.stringify(this.form)});
      if (!results.error) {
        this.show = false;
        this.$emit('done');
      }
    }
  }
}
</script>